import { useState } from "react";
import { Check } from "@phosphor-icons/react";
import VideoMain from "../video/VideoMain";

interface ITask {
  label: string | React.ReactNode;
}

interface IStep {
  id: number;
  title: string;
  description?: string | React.ReactNode;
  videoUrl?: string;
  tasks: ITask[];
}

const obKey = "kaizu_fullSelf_ob";

const OnboardingMain = ({
  onComplete,
}: {
  onComplete: (s: boolean) => void;
}) => {
  // localStorage yerine state kullanıyoruz (demo için)
  const [isOnboardingComplete, setIsOnboardingComplete] =
    useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [completedTasks, setCompletedTasks] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const steps: IStep[] = [
    {
      id: 1,
      title: "1. Program akışı ve senden beklediklerimiz",
      tasks: [{ label: "Videoyu izledim." }],
      videoUrl: "https://vimeo.com/1104880342",
    },
    {
      id: 2,
      title: "2. İletişim için Pumble uygulaması",
      description: (
        <>
          Ders içerikleri, projeler ve diğer tüm konularda haberleşmek için
          Pumble uygulamasını kullanıyoruz.
          <ul className="list-disc pl-8 pt-2">
            <li>
              Önce{" "}
              <a
                href="https://code2work.co/join-pumble"
                target="_blank"
                rel="noopener noreferrer"
                className="text-myGreen font-semibold underline"
              >
                https://code2work.co/join-pumble
              </a>{" "}
              linkinden kayıt ol,
            </li>
            <li>
              Sonra{" "}
              <a
                href="https://pumble.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-myGreen font-semibold underline"
              >
                Pumble uygulamasını
              </a>{" "}
              indir ve giriş yap.
            </li>
          </ul>
        </>
      ),
      tasks: [
        {
          label: (
            <>
              Pumble{" "}
              <a
                href="https://code2work.co/join-pumble"
                target="_blank"
                rel="noopener noreferrer"
                className="text-myGreen font-semibold underline"
              >
                kayıt linki ile
              </a>{" "}
              kayıt oldum.
            </>
          ),
        },
        {
          label: (
            <>
              <a
                href="https://pumble.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-myGreen font-semibold underline"
              >
                Pumble uygulamasını
              </a>{" "}
              indirdim ve giriş yaptım.
            </>
          ),
        },
        { label: "#random kanalına ilk mesajımı gönderdim." },
      ],
    },
    {
      id: 3,
      title: "3. Kaizu Sistemi Tanıtımı",
      videoUrl: "https://vimeo.com/1104963472",
      tasks: [{ label: "Videoyu izledim." }],
    },
  ];

  const handleTaskChange = (
    stepId: number,
    order: number,
    checked: boolean
  ): void => {
    setCompletedTasks((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [order]: checked,
      },
    }));
  };

  const isStepComplete = (stepId: number): boolean => {
    const step = steps.find((s) => s.id === stepId);
    if (!step) return false;

    if (completedTasks[stepId]) {
      return (
        step.tasks.length ===
        Object.values(completedTasks?.[stepId])?.filter((s) => s).length
      );
    } else {
      return false;
    }
  };

  const handleNextStep = (): void => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      localStorage.setItem(obKey, "true");
      setIsOnboardingComplete(true);

      setTimeout(() => {
        console.log("here");
        onComplete(false);
      }, 3000);
    }
  };

  /* const handleStartOnboarding = (): void => {
    setIsOnboardingComplete(false);
    setCurrentStep(1);
    setCompletedTasks({});
  }; */

  if (isOnboardingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 py-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Tebrikler! 🎉
          </h2>
          <p className="text-gray-600">
            Derslere başlamaya hazırsın. Ana sayfaya yönlendiriyorum.
          </p>
          {/* <button
            onClick={handleStartOnboarding}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors mt-6"
          >
            Demo için Tekrar Başlat
          </button> */}
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep - 1];

  return (
    <div className="flex flex-col justify-start h-screen py-8 overflow-auto">
      <div className="max-w-xl lg:max-w-6xl m-auto w-full flex flex-col lg:flex-row items-stretch lg:items-start gap-2 px-2">
        {/* Progress Bar */}
        <div className="panel rounded-3xl p-8 border border-myGb/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-myOrange mb-2">
                Hoş geldin!
              </h2>
              <h1 className="text-lg font-medium">
                Başlamadan önce lütfen videoları izle ve görevleri tamamla.
              </h1>
            </div>
          </div>

          <div className="flex space-x-2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex-1">
                <div
                  className={`h-2 rounded-full transition-colors ${index + 1 < currentStep
                    ? "bg-myGreen/60"
                    : index + 1 === currentStep
                      ? "bg-myGreen"
                      : "bg-mySoftBg/15"
                    }`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="panel rounded-3xl shadow-xl max-w-2xl w-full border border-myGb/30">
          {/* Step Content */}
          <div className="p-8 lg:p-10">
            <h2 className="text-xl font-bold text-myOrange pt-1">
              {currentStepData.title}
            </h2>

            {currentStepData.description && (
              <p className="mt-2 text-white">{currentStepData.description}</p>
            )}

            {/* Video Placeholder */}
            {currentStepData.videoUrl ? (
              <div className="aspect-video flex mb-6 mt-4 rounded-xl border border-myGb/30 overflow-hidden">
                <VideoMain
                  fitParent
                  url={currentStepData.videoUrl}
                  // url={"https://youtu.be/jyKoCSTysSQ"}
                  setTime={() => { }}
                  isLive={false}
                  onEnd={() => handleTaskChange(currentStep, 0, true)}
                  key={currentStepData.title}
                />
              </div>
            ) : (
              <div className="mb-6" />
            )}

            {/* Checklist */}
            <div className="space-y-3 mb-8 flex">
              <h3 className="font-semibold pl-6 w-32 text-myOrange">
                Görevler
              </h3>

              <div className="flex-1">
                {currentStepData.tasks.map((task, order) => {
                  const isChecked =
                    completedTasks[currentStep]?.[order] || false;
                  return (
                    <label
                      key={order}
                      className="flex items-center cursor-pointer group"
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) =>
                            handleTaskChange(
                              currentStep,
                              order,
                              e.target.checked
                            )
                          }
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded border-2 transition-colors ${isChecked
                            ? "bg-myGreen border-myGreen"
                            : "border-gray-300 group-hover:border-gray-400"
                            }`}
                        >
                          {isChecked && (
                            <Check className="w-5 h-5 text-black absolute inset-0" />
                          )}
                        </div>
                      </div>
                      <span
                        className={`ml-3 transition-colors ${isChecked ? "text-myGreen line-through" : ""
                          }`}
                      >
                        {task.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Next Button */}
            <button
              onClick={handleNextStep}
              disabled={!isStepComplete(currentStep)}
              className={`w-full bg-myBlue py-3 px-4 rounded-lg font-medium transition-colors ${isStepComplete(currentStep)
                ? "text-white hover:bg-myBlue/90 cursor-pointer"
                : "opacity-60 cursor-not-allowed"
                }`}
            >
              {currentStep === steps.length
                ? "Kurulumu Tamamla"
                : "Sonraki Adım"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingMain;
